'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { EASE, HAIRLINE, restoreDashes, useReveal } from '@/components/page-kit'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { ACCOUNTS } from './data'

/* Accounts' device is the centre axis — the T-account: a vertical rule with
   the item hard against one side of it and its counterpart against the other.
   It carries the modules section and the capability list.

   That is the deliberate contrast with the neighbouring pages: UDC is a wired
   bus of parallel modules, HRMS a directional lifecycle track, and Payroll a
   statement whose amounts run out to the far right edge. Here everything
   hinges on a single centre line.

   The hero's figures are right-aligned for the same reason — amounts belong on
   a balance column, which is also what tells this band apart from Payroll's.

   The h1 appends the audience to the authored title: "Accounting Management
   System" alone says nothing about who it is for, and every term this page
   competes for carries a school or college in it. */

export function AccountsHero() {
  const band = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[80svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Products', href: '/products' },
              { name: ACCOUNTS.title, href: `/products/${ACCOUNTS.slug}` },
            ]}
          />

          <h1
            className={cn(
              'font-heading mt-6 max-w-4xl font-normal tracking-tight text-foreground',
              'text-[32px]/[1.14] sm:text-[44px]/[1.14] md:text-5xl/[1.12] lg:text-[60px]/[1.1]'
            )}
          >
            <ChromaText>{`${ACCOUNTS.title} for schools and colleges`}</ChromaText>
          </h1>

          <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl md:text-2xl dark:text-primary-active">
            {ACCOUNTS.tagline}
          </p>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            {restoreDashes(ACCOUNTS.description)}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="#included">See what's included</a>
            </Button>
          </div>
        </div>
      </div>

      {/* ── The figures ────────────────────────────────────────────────────
          Column ruling runs the full height of the band rather than sitting
          inset, so it reads as a ruled table — the same ledger logic as the
          T-account below. (Payroll's band uses inset rules and leads with the
          figure; this one leads with the label. Both stay left-aligned:
          right-aligning cells side by side just crams each one against the
          rule on its right and leaves a hole on its left.)

          The rule runs full-bleed while the cells stay in the container, so the
          band lines up with the headline above it. */}
      <div ref={band.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="container mx-auto">
          <dl className="grid grid-cols-2 lg:grid-cols-4">
            {ACCOUNTS.stats.map((s, i) => (
              <div
                key={s.label}
                className={cn(
                  'relative py-5 pr-5 sm:py-6 sm:pr-6',
                  // Only cells that actually carry a rule get padded off it,
                  // so the first cell of every row stays flush with the
                  // headline's left edge.
                  i % 2 === 1 && 'pl-5 sm:pl-6',
                  i > 0 && 'lg:pl-6'
                )}
                style={{
                  opacity: band.shown ? 1 : 0,
                  transform: band.shown ? 'none' : 'translateY(8px)',
                  transition: `opacity 460ms ${EASE} ${i * 80}ms, transform 460ms ${EASE} ${i * 80}ms`,
                }}
              >
                {i > 0 && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-y-0 left-0 w-px bg-border/70',
                      i % 2 === 0 && 'hidden lg:block'
                    )}
                  />
                )}
                <dt className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {s.label}
                </dt>
                <dd className="font-heading mt-2 text-2xl font-normal tabular-nums leading-none text-foreground sm:text-3xl">
                  <AnimatedNumber value={s.value} delay={i * 70} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  )
}
